import { useEffect } from "react";
import { useParams, Navigate } from "react-router";
import { AppShell, MarqueeHeader } from "../layout";
import { DateSelector } from "../components/DateSelector";
import { useAuth } from "../auth";
import { parseRoleSlug, roleHudLabel, roleLabel, roleSlug } from "../auth/roleMap";
import { DcGridScreen } from "./DcGridScreen";
import { OoAssignScreen } from "./OoAssignScreen";
import { OperatorScreen } from "./OperatorScreen";
import { EoScreen } from "./eo/EoScreen";
import css from "./RoleDashboard.module.css";

/**
 * Stage 1d placeholder — shown after auth + role pick. Real role-specific
 * screens (DC Booking Grid, OO Assignments, Operator Dashboard, EO Branch
 * Settings) land in Stages 2–4.
 *
 * Responsibilities for now:
 *  - Validate the URL slug matches an available role; redirect otherwise.
 *  - Mirror the chosen role into <html data-role> so all role tokens cascade.
 *  - Render AppShell + Header + DateSelector so the visual scaffold is real.
 *  - Show a clear "сюда подъедет такой-то экран" placeholder body.
 */
export function RoleDashboard() {
  const { roleSlug: slug } = useParams<{ roleSlug: string }>();
  const { user, availableRoles, activeRole, setActiveRole } = useAuth();
  const role = parseRoleSlug(slug ?? "");

  /**
   * Only sync the active role when the URL points to a role the user actually
   * has. Writing activeRole for an unavailable slug used to create an infinite
   * redirect loop: effect sets activeRole → <Navigate to="/"> → AuthGate reads
   * the just-set activeRole → navigates back to the same slug → repeat
   * ("Maximum update depth exceeded").
   */
  const roleAvailable = !!role && availableRoles.includes(role);
  useEffect(() => {
    if (roleAvailable && role) {
      document.documentElement.dataset.role = roleSlug(role);
      if (role !== activeRole) setActiveRole(role);
    }
  }, [roleAvailable, role, activeRole, setActiveRole]);

  if (!role || !availableRoles.includes(role)) {
    return <Navigate to="/" replace />;
  }

  /* DC, OO, OP, EO all have their real screens now. */
  if (role === "DC") return <DcGridScreen />;
  if (role === "OO") return <OoAssignScreen />;
  if (role === "OP") return <OperatorScreen />;
  if (role === "EO") return <EoScreen />;

  return (
    <AppShell
      header={
        <MarqueeHeader
          roleLabel={roleHudLabel(role)}
          nick={user?.nickname || user?.name || "guest"}
          dateSelector={<DateSelector mode="day" />}
        />
      }
    >
      <section className={css.body}>
        <p className={css.kicker}>STAGE 1D · DASHBOARD PLACEHOLDER</p>
        <h2 className={css.h}>{roleLabel(role)}</h2>
        {/* keep the long Russian label in the placeholder body */}
        <p className={css.lead}>
          Реальный экран этой роли подъедет в следующих стадиях:
          DC — Stage 2, Operator — Stage 3, OO — Stage 4, EO — backlog.
        </p>

        {availableRoles.length > 1 ? (
          <SwitchRoleRow />
        ) : null}
      </section>
    </AppShell>
  );
}

function SwitchRoleRow() {
  const { availableRoles, activeRole, setActiveRole } = useAuth();
  return (
    <div className={css.switchRow}>
      <span className={css.switchLabel}>СМЕНИТЬ РОЛЬ</span>
      {availableRoles.map((r) => (
        <button
          key={r}
          type="button"
          className={`${css.switchBtn} ${activeRole === r ? css.switchOn : ""}`}
          onClick={() => {
            setActiveRole(r);
            window.history.pushState(null, "", `/${roleSlug(r)}`);
            document.documentElement.dataset.role = roleSlug(r);
          }}
        >
          {r}
        </button>
      ))}
    </div>
  );
}
